import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import T from "../components/T";
import { Button } from "../components/ui/button";
import { ChevronLeft, Shield, Wallet, Leaf, Heart, Compass, Sun, PawPrint, Moon, Sparkles, Check } from "lucide-react";
import { listPaths } from "../data/paths";
import { getActivePath, setActivePath } from "../lib/localProgress";

const ICONS = {
  shield: Shield, wallet: Wallet, leaf: Leaf, heart: Heart,
  compass: Compass, sun: Sun, paw: PawPrint, moon: Moon, sparkles: Sparkles,
};

export default function PathPicker() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [active, setActive] = useState(getActivePath());

  const choose = (slug) => {
    setActivePath(slug);
    setActive(slug);
    setTimeout(() => navigate("/today"), 250);
  };

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-path-picker">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("v11.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("v11.path_picker.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight"><T i18nKey="v11.path_picker.h" /></h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("v11.path_picker.sub")}</p>
      </div>

      <div className="px-8 space-y-2.5 pb-10">
        {listPaths().map((p) => {
          const Icon = ICONS[p.icon] || Sparkles;
          const isActive = active === p.slug;
          return (
            <button
              key={p.slug}
              data-testid={`path-${p.slug}`}
              onClick={() => choose(p.slug)}
              className={`w-full text-left rounded-3xl p-5 border transition-all flex items-start gap-4 ${
                isActive
                  ? "bg-brand text-white border-brand shadow-md"
                  : "bg-card border-border hover:border-brand/50"
              }`}
            >
              <span className={`w-10 h-10 grid place-items-center rounded-full shrink-0 ${
                isActive ? "bg-white/15 text-white" : "bg-brand/10 text-brand"
              }`}>
                <Icon className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="font-serif text-lg">{lang === "es" ? p.label_es : p.label_en}</div>
                <p className={`text-sm mt-1 leading-relaxed ${isActive ? "text-white/85" : "text-foreground/65"}`}>
                  {lang === "es" ? p.desc_es : p.desc_en}
                </p>
              </div>
              {isActive && <Check className="w-5 h-5 mt-2" />}
            </button>
          );
        })}

        {active && (
          <Button
            data-testid="path-continue-btn"
            onClick={() => navigate("/today")}
            className="w-full mt-4 h-13 rounded-full bg-brand hover:bg-brand/90 text-white"
          >
            {t("v11.path_picker.selected")} →
          </Button>
        )}
      </div>
    </AppShell>
  );
}
